import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

const TYPES = [
  { id: 'social_post', label: 'Social post', icon: 'fa-square-share-nodes' },
  { id: 'schedule',    label: 'Schedule',    icon: 'fa-calendar-check' },
  { id: 'event',       label: 'Event',       icon: 'fa-calendar-day' },
  { id: 'appearance',  label: 'Appearance',  icon: 'fa-microphone' },
  { id: 'interview',   label: 'Interview',   icon: 'fa-user-tie' },
  { id: 'press',       label: 'Press',       icon: 'fa-newspaper' },
  { id: 'budget',      label: 'Budget',      icon: 'fa-file-invoice-dollar' },
  { id: 'other',       label: 'Other',       icon: 'fa-file' },
];
const TYPE_BY_ID = Object.fromEntries(TYPES.map(t => [t.id, t]));

const STATUS_TABS = [
  { id: 'pending',  label: 'Pending',  tint: '#F59E0B' },
  { id: 'review',   label: 'Review',   tint: '#EF4444' },
  { id: 'approved', label: 'Approved', tint: '#10B981' },
  { id: 'all',      label: 'All',      tint: '#64748B' },
];

// Partial isn't a real server status (server only emits pending/review/approved)
// — it's a client-derived label for "approved overall but not every attachment
// was individually marked approve". Same amber palette as pending so the whole
// UI agrees: something's still open, check it.
const PARTIAL_STATUS_META = { id: 'partial', label: 'Partial', tint: '#F59E0B' };

function attachmentMarkSummary(item) {
  const atts = item?.attachments || [];
  const decisions = item?.decisions || [];
  if (atts.length === 0 || decisions.length === 0) return null;
  let approved = 0, denied = 0, unmarked = 0;
  for (const a of atts) {
    const m = aggregateAttachmentMark(item, a.url);
    if (m === 'approve') approved += 1;
    else if (m === 'deny') denied += 1;
    else unmarked += 1;
  }
  return { approved, denied, unmarked, total: atts.length };
}

function isPartiallyApproved(item) {
  if (item?.status !== 'approved') return false;
  const s = attachmentMarkSummary(item);
  return !!s && s.approved < s.total;
}

// Compact "✓ foo.jpg, bar.jpg +N more" label for the list card — spells out
// WHICH files were approved so browsing doesn't require opening detail.
function approvedAttachmentNames(item, maxNames = 2) {
  const atts = item?.attachments || [];
  const approved = atts.filter(a => aggregateAttachmentMark(item, a.url) === 'approve');
  if (approved.length === 0) return null;
  const names = approved.slice(0, maxNames).map(a => a.name);
  const extra = approved.length - names.length;
  return extra > 0 ? `${names.join(', ')} +${extra} more` : names.join(', ');
}

function displayStatus(item) {
  return isPartiallyApproved(item) ? 'partial' : item?.status;
}

function statusMetaFor(item) {
  if (isPartiallyApproved(item)) return PARTIAL_STATUS_META;
  return STATUS_TABS.find(t => t.id === item?.status) || STATUS_TABS[3];
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const isStandalone = !window.location.pathname.startsWith('/admin');
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [recentApproved, setRecentApproved] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, approved, artistList] = await Promise.all([
        apiFetch(`/approvals?status=${status}`),
        apiFetch('/approvals?status=approved'),
        apiFetch('/artists'),
      ]);
      setItems(list);
      setRecentApproved(approved.slice(0, 5));
      setArtists(artistList);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Quietly re-poll every 30s so decisions made by viewers appear without
  // needing to reload the page or click into an item.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 960, margin: '0 auto' }}>
      {isStandalone && (
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 14, fontWeight: 600,
                   cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0', marginBottom: 8,
                   display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} /> Back
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Approvals</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', margin: '4px 0 0' }}>
            {isAdmin
              ? 'Post items for artists to approve, deny, or comment on.'
              : 'Review and respond to items waiting on you.'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setCreating(true)}
            style={{ padding: '10px 18px', borderRadius: 12, background: 'var(--blue)', color: '#fff',
                     border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
            + New Approval
          </button>
        )}
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(t => {
          const active = status === t.id;
          return (
            <button key={t.id} onClick={() => setStatus(t.id)}
              style={{
                padding: '8px 16px', borderRadius: 999, border: '1px solid var(--border)',
                background: active ? t.tint : 'var(--card)',
                color: active ? '#fff' : 'var(--text2)',
                fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState status={status} onCreate={isAdmin ? () => setCreating(true) : null} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              artists={artists}
              onOpen={() => setEditing(item)}
            />
          ))}
        </div>
      )}

      {/* Recently approved strip — always visible so admins can see results
          without switching tabs. Hidden from the Approved tab (would duplicate). */}
      {status !== 'approved' && recentApproved.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
                          textTransform: 'uppercase', color: 'var(--text3)' }}>
              <i className="fa-solid fa-circle-check" style={{ color: '#10B981', marginRight: 6 }} />
              Recently approved
            </div>
            <button onClick={() => setStatus('approved')}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', fontWeight: 700,
                       fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              See all →
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recentApproved.map(item => (
              <ItemCard
                key={item.id}
                item={item}
                artists={artists}
                onOpen={() => setEditing(item)}
              />
            ))}
          </div>
        </div>
      )}

      {creating && (
        <CreateApprovalModal
          artists={artists}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <ApprovalDetailModal
          item={editing}
          artists={artists}
          currentUser={user}
          isAdmin={isAdmin}
          onClose={() => { setEditing(null); load(); }}
          onChanged={() => { load(); }}
          onRevised={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Item card ─────────────────────────────────────────────────────────────────

function ItemCard({ item, artists, onOpen }) {
  const type = TYPE_BY_ID[item.type] || TYPE_BY_ID.other;
  const artistNames = (item.artistIds || [])
    .map(id => (artists.find(a => a.id === id) || {}).name)
    .filter(Boolean)
    .join(', ');
  const statusMeta = statusMetaFor(item);
  const partialSummary = isPartiallyApproved(item) ? attachmentMarkSummary(item) : null;

  const approverCount = (item.approverIds || []).length;
  const decisions = item.decisions || [];
  const approvedCount = decisions.filter(d => d.action === 'approve').length;
  const deniedCount = decisions.filter(d => d.action === 'deny').length;
  const pendingCount = approverCount - approvedCount - deniedCount;

  return (
    <button onClick={onOpen} style={{
      textAlign: 'left', padding: 16, borderRadius: 14, background: 'var(--card)',
      border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
      display: 'flex', alignItems: 'flex-start', gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: 'rgba(30,64,175,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <i className={`fa-solid ${type.icon}`} style={{ color: 'var(--blue)', fontSize: 16 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
            color: '#fff', background: statusMeta.tint, padding: '2px 8px', borderRadius: 999,
          }}>{displayStatus(item)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 0.5 }}>
            {type.label.toUpperCase()}
          </span>
          {item.version > 1 && (
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)' }}>v{item.version}</span>
          )}
        </div>
        {partialSummary && (
          <>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 800, color: '#9A6300',
              background: '#FEF3C7', border: '1px solid rgba(245,158,11,0.5)',
              padding: '3px 8px', borderRadius: 999, marginBottom: 6,
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 9 }} />
              {partialSummary.approved} of {partialSummary.total} approved
              {partialSummary.denied > 0 ? ` · ${partialSummary.denied} denied` : ''}
              {partialSummary.unmarked > 0 ? ` · ${partialSummary.unmarked} pending` : ''}
            </div>
            {(() => {
              const approvedNames = approvedAttachmentNames(item, 2);
              return approvedNames ? (
                <div style={{ fontSize: 12, fontWeight: 600, color: '#10B981',
                              marginBottom: 8, whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <i className="fa-solid fa-check" style={{ marginRight: 5 }} />
                  {approvedNames}
                </div>
              ) : null;
            })()}
          </>
        )}
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          {item.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
          {artistNames || 'No artist'} · {new Date(item.createdAt).toLocaleDateString()}
        </div>

        {/* Progress summary — "2 of 3 approved · 1 pending" */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>
          <span style={{ color: '#10B981' }}>
            <i className="fa-solid fa-check" style={{ marginRight: 4 }} />
            {approvedCount} of {approverCount} approved
          </span>
          {deniedCount > 0 && (
            <span style={{ color: '#EF4444' }}>
              <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />
              {deniedCount} denied
            </span>
          )}
          {pendingCount > 0 && (
            <span style={{ color: '#94A3B8' }}>
              <i className="fa-regular fa-clock" style={{ marginRight: 4 }} />
              {pendingCount} waiting
            </span>
          )}
        </div>

        <DecisionChips item={item} large />
      </div>
    </button>
  );
}

function DecisionChips({ item, large = false }) {
  const approvers = item.approverIds || [];
  const byUser = new Map((item.decisions || []).map(d => [d.userId, d]));
  const names = item.approverNames || {};
  const fontSize = large ? 12 : 11;
  const padding = large ? '4px 10px' : '2px 8px';
  const iconSize = large ? 10 : 9;
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: large ? 4 : 8, flexWrap: 'wrap' }}>
      {approvers.map(uid => {
        const d = byUser.get(uid);
        const color = d?.action === 'approve' ? '#10B981'
                    : d?.action === 'deny'    ? '#EF4444'
                    :                           '#94A3B8';
        const label = d?.userName || names[uid] || 'Approver';
        const icon = d?.action === 'approve' ? 'fa-circle-check'
                   : d?.action === 'deny'    ? 'fa-circle-xmark'
                   :                           'fa-clock';
        // Decided chips render as solid color; pending stays soft so it's clear at a glance.
        const isDecided = !!d;
        return (
          <span key={uid} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize, fontWeight: 700,
            color: isDecided ? '#fff' : color,
            background: isDecided ? color : color + '18',
            border: `1px solid ${isDecided ? color : color + '40'}`,
            borderRadius: 999, padding,
          }}>
            <i className={`fa-solid ${icon}`} style={{ fontSize: iconSize }} />
            {label}
          </span>
        );
      })}
    </div>
  );
}

function EmptyState({ status, onCreate }) {
  const msg = {
    pending:  'Nothing awaiting a decision.',
    review:   'No items need a revision right now.',
    approved: 'No approved items yet.',
    all:      'No approvals created yet.',
  }[status];
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text3)' }}>
      <i className="fa-solid fa-circle-check" style={{ fontSize: 40, marginBottom: 12 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{msg}</div>
      {onCreate && (
        <button onClick={onCreate} style={{
          marginTop: 10, padding: '8px 16px', borderRadius: 10, background: 'var(--blue)',
          color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
        }}>+ New Approval</button>
      )}
    </div>
  );
}

// ─── Create modal ──────────────────────────────────────────────────────────────

function CreateApprovalModal({ artists, onClose, onCreated }) {
  const [type, setType] = useState('social_post');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Each artist routes to its own linked user (artist.userId). Surface any
  // artists missing a user so admin can fix it before submit.
  const selectedArtists = selectedArtistIds
    .map(id => artists.find(a => a.id === id))
    .filter(Boolean);
  const missingUserArtists = selectedArtists.filter(a => !a.userId);

  const toggleArtist = (id) => {
    setSelectedArtistIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async () => {
    setErr('');
    if (!title.trim()) return setErr('Title is required.');
    if (selectedArtistIds.length === 0) return setErr('Pick at least one artist.');
    if (missingUserArtists.length > 0) {
      return setErr(`Link a user account for ${missingUserArtists.map(a => a.name).join(', ')} first (Artists tab → Edit).`);
    }
    setSaving(true);
    try {
      await apiFetch('/approvals', {
        method: 'POST',
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim() || null,
          attachments,
          artistIds: selectedArtistIds,
          teamViewerIds: [],
        }),
      });
      onCreated();
    } catch (e) {
      setErr(e.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const isMobile = window.innerWidth < 768;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
                   display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--card)', borderRadius: isMobile ? '20px 20px 0 0' : 16,
        border: '1px solid var(--border)', borderBottom: isMobile ? 'none' : undefined,
        width: '100%', maxWidth: isMobile ? '100%' : 560, maxHeight: '92vh', overflowY: 'auto',
        padding: isMobile ? '12px 20px 20px' : 24,
        paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : 24,
      }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>New Approval</h2>

        <Label>Type</Label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {TYPES.map(t => (
            <button key={t.id} onClick={() => setType(t.id)} type="button"
              style={{
                padding: '8px 14px', borderRadius: 999, border: '1px solid var(--border)',
                background: type === t.id ? 'var(--blue)' : 'var(--bg)',
                color: type === t.id ? '#fff' : 'var(--text2)',
                fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize: 11 }} />
              {t.label}
            </button>
          ))}
        </div>

        <Label>Title</Label>
        <input value={title} onChange={e => setTitle(e.target.value)}
          placeholder="What are you asking approval for?"
          style={inputStyle} />

        <Label>Description</Label>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
          rows={4}
          placeholder="Context, caption copy, budget notes, etc."
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />

        <Label>Artists (who is this for?)</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {artists.map(a => {
            const sel = selectedArtistIds.includes(a.id);
            return (
              <button key={a.id} onClick={() => toggleArtist(a.id)} type="button"
                style={{
                  padding: '6px 12px', borderRadius: 999, border: '1px solid var(--border)',
                  background: sel ? a.color || 'var(--blue)' : 'var(--bg)',
                  color: sel ? '#fff' : 'var(--text2)',
                  fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                }}>{a.name}</button>
            );
          })}
        </div>

        <Label>Routing</Label>
        {selectedArtists.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, padding: 12,
                        background: 'var(--bg)', border: '1px dashed var(--border)', borderRadius: 10 }}>
            Select an artist above — the request will be sent to that artist's linked user for approval.
          </div>
        ) : missingUserArtists.length > 0 ? (
          <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 14, padding: 12,
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10 }}>
            <strong>{missingUserArtists.map(a => a.name).join(', ')}</strong> {missingUserArtists.length === 1 ? 'has' : 'have'} no user account linked. Open the Artists tab and set the account before sending.
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, padding: 12,
                        background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10 }}>
            <i className="fa-solid fa-arrow-right" style={{ marginRight: 8, color: '#10B981' }} />
            Will be sent to <strong>{selectedArtists.map(a => a.name).join(', ')}</strong>{selectedArtists.length > 1 ? ' — all must approve' : ''}.
          </div>
        )}

        <Label>Attachments</Label>
        <AttachmentDropzone
          attachments={attachments}
          setAttachments={setAttachments}
          onError={setErr}
        />

        {err && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '10px 18px', borderRadius: 10, background: 'var(--bg)',
                     color: 'var(--text2)', border: '1px solid var(--border)', fontWeight: 700,
                     fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={submit} disabled={saving}
            style={{ padding: '10px 18px', borderRadius: 10, background: 'var(--blue)',
                     color: '#fff', border: 'none', fontWeight: 700, fontSize: 14,
                     cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Sending…' : 'Send for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail modal ──────────────────────────────────────────────────────────────

function ApprovalDetailModal({ item, artists, currentUser, isAdmin, onClose, onChanged, onRevised }) {
  const [revising, setRevising] = useState(false);
  const [fresh, setFresh] = useState(item);
  const [decisionBusy, setDecisionBusy] = useState(null); // 'approve' | 'deny' | null
  const [comment, setComment] = useState('');
  const [decisionErr, setDecisionErr] = useState('');

  useEffect(() => {
    apiFetch(`/approvals/${item.id}`).then(setFresh).catch(() => {});
  }, [item.id]);

  const type = TYPE_BY_ID[fresh.type] || TYPE_BY_ID.other;
  const artistNames = (fresh.artistIds || [])
    .map(id => (artists.find(a => a.id === id) || {}).name).filter(Boolean).join(', ');

  // An approver can decide if they're on the list and item isn't already approved.
  // (Backend re-validates; this just controls whether we show the buttons.)
  const myUserId = currentUser?.id;
  const isApprover = (fresh.approverIds || []).includes(myUserId);
  const myDecision = (fresh.decisions || []).find(d => d.userId === myUserId);
  const canDecide = isApprover && fresh.status !== 'approved';

  const submitDecision = async (action) => {
    setDecisionBusy(action); setDecisionErr('');
    try {
      const updated = await apiFetch(`/approvals/${fresh.id}/decision`, {
        method: 'POST',
        body: JSON.stringify({ action, comment: comment.trim() || undefined }),
      });
      setFresh({ ...fresh, ...updated });
      setComment('');
      onChanged();
    } catch (e) {
      setDecisionErr(e.message || 'Failed to submit');
    } finally {
      setDecisionBusy(null);
    }
  };

  const del = async () => {
    if (!window.confirm('Delete this approval item? Cannot be undone.')) return;
    await apiFetch(`/approvals/${fresh.id}`, { method: 'DELETE' });
    onClose();
    onChanged();
  };

  const isMobile = window.innerWidth < 768;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
                   display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 20 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--card)', borderRadius: isMobile ? '20px 20px 0 0' : 16,
        border: '1px solid var(--border)', borderBottom: isMobile ? 'none' : undefined,
        width: '100%', maxWidth: isMobile ? '100%' : 560, maxHeight: '92vh', overflowY: 'auto',
        padding: isMobile ? '12px 20px 20px' : 24,
        paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <i className={`fa-solid ${type.icon}`} style={{ color: 'var(--blue)', fontSize: 18 }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1,
                         textTransform: 'uppercase', color: 'var(--text3)' }}>
            {type.label}{fresh.version > 1 ? ` · v${fresh.version}` : ''}
          </span>
          <StatusPill status={displayStatus(fresh)} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{fresh.title}</h2>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
          {artistNames || 'No artist'} · Created by {fresh.createdByName || 'Admin'} on {new Date(fresh.createdAt).toLocaleString()}
        </div>

        {isPartiallyApproved(fresh) && (() => {
          const s = attachmentMarkSummary(fresh);
          const openParts = [];
          if (s.denied > 0) openParts.push(`${s.denied} denied`);
          if (s.unmarked > 0) openParts.push(`${s.unmarked} not marked`);
          return (
            <div style={{
              display: 'flex', gap: 12, alignItems: 'flex-start',
              background: '#FEF3C7', border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: 12, padding: 12, marginBottom: 16, color: '#6B4300',
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 20, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 2 }}>Partial approval</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>
                  {s.approved} of {s.total} files were approved.
                  {openParts.length ? ` Still open: ${openParts.join(', ')}.` : ''}
                </div>
              </div>
            </div>
          );
        })()}

        {fresh.description && (
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
            padding: 12, fontSize: 14, color: 'var(--text2)', marginBottom: 16, whiteSpace: 'pre-wrap',
          }}>{fresh.description}</div>
        )}

        {(fresh.attachments || []).length > 0 && (
          <AttachmentsBlock item={fresh} />
        )}

        <Label>Decisions</Label>
        <div style={{ marginBottom: 16 }}>
          <DecisionChips item={fresh} />
          {(fresh.decisions || []).filter(d => d.comment).map(d => (
            <div key={d.userId} style={{
              marginTop: 8, fontSize: 12, color: 'var(--text2)', padding: 10,
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
            }}>
              <strong style={{ color: 'var(--text)' }}>{d.userName}:</strong> {d.comment}
            </div>
          ))}
        </div>

        {canDecide && (
          <div style={{ marginBottom: 16, padding: 14, borderRadius: 12,
                        background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <Label>{myDecision ? 'Change your decision' : 'Your decision'}</Label>
            {myDecision && (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                You {myDecision.action === 'approve' ? 'approved' : 'denied'} this
                {myDecision.comment ? ` — "${myDecision.comment}"` : ''}.
              </div>
            )}
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
              placeholder="Optional comment…"
              style={{ ...inputStyle, minHeight: 52, marginBottom: 10, resize: 'vertical' }} />
            {decisionErr && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 8 }}>{decisionErr}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => submitDecision('approve')} disabled={!!decisionBusy}
                style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#10B981',
                         color: '#fff', border: 'none', fontWeight: 800, fontSize: 14,
                         cursor: 'pointer', fontFamily: 'inherit', opacity: decisionBusy ? 0.5 : 1 }}>
                <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                {decisionBusy === 'approve' ? 'Approving…' : 'Approve'}
              </button>
              <button onClick={() => submitDecision('deny')} disabled={!!decisionBusy}
                style={{ flex: 1, padding: '12px', borderRadius: 10, background: '#EF4444',
                         color: '#fff', border: 'none', fontWeight: 800, fontSize: 14,
                         cursor: 'pointer', fontFamily: 'inherit', opacity: decisionBusy ? 0.5 : 1 }}>
                <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }} />
                {decisionBusy === 'deny' ? 'Denying…' : 'Deny'}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          {isAdmin ? (
            <button onClick={del}
              style={{ background: 'none', border: 'none', color: '#EF4444', fontWeight: 700,
                       fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              <i className="fa-solid fa-trash" style={{ marginRight: 4 }} /> Delete
            </button>
          ) : <div />}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose}
              style={{ padding: '10px 18px', borderRadius: 10, background: 'var(--bg)',
                       color: 'var(--text2)', border: '1px solid var(--border)', fontWeight: 700,
                       fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
            {isAdmin && fresh.status === 'review' && !revising && (
              <button onClick={() => setRevising(true)}
                style={{ padding: '10px 18px', borderRadius: 10, background: 'var(--blue)',
                         color: '#fff', border: 'none', fontWeight: 700, fontSize: 14,
                         cursor: 'pointer', fontFamily: 'inherit' }}>
                Post revision
              </button>
            )}
          </div>
        </div>

        {revising && (
          <ReviseBlock
            original={fresh}
            onCancel={() => setRevising(false)}
            onRevised={onRevised}
          />
        )}
      </div>
    </div>
  );
}

function ReviseBlock({ original, onCancel, onRevised }) {
  const [title, setTitle] = useState(original.title);
  const [description, setDescription] = useState(original.description || '');
  const [attachments, setAttachments] = useState(original.attachments || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/approvals/${original.id}/revise`, {
        method: 'POST',
        body: JSON.stringify({ title, description, attachments }),
      });
      onRevised();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>
        Revision — v{(original.version || 1) + 1}
      </h3>
      <Label>Title</Label>
      <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
      <Label>Description</Label>
      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} />
      <Label>Attachments</Label>
      <AttachmentDropzone
        attachments={attachments}
        setAttachments={setAttachments}
        onError={setErr}
        compact
      />
      {err && <div style={{ color: '#EF4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} disabled={saving}
          style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--bg)',
                   color: 'var(--text2)', border: '1px solid var(--border)', fontWeight: 700,
                   fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={submit} disabled={saving}
          style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--blue)',
                   color: '#fff', border: 'none', fontWeight: 700, fontSize: 13,
                   cursor: 'pointer', fontFamily: 'inherit' }}>
          {saving ? 'Posting…' : 'Post revision'}
        </button>
      </div>
    </div>
  );
}

// Collapse every approver's per-attachment marks on `item` down to a single
// state for one attachment URL. Deny wins — if any approver marked the file
// "deny", show the ✕ badge. Otherwise if anyone marked it "approve", show ✓.
// Returns null when no approver has marked this attachment yet.
//
// Falls back to parsing the "Needs revision: foo\nOK: bar" comment prefix
// when the structural `attachmentDecisions` field is missing (older server
// builds silently drop it, but the prefix still lands in the comment).
function aggregateAttachmentMark(item, url) {
  const decisions = item?.decisions || [];
  const att = (item?.attachments || []).find(a => a.url === url);
  const name = att?.name;
  let sawApprove = false;
  for (const d of decisions) {
    let m = d.attachmentDecisions ? d.attachmentDecisions[url] : null;
    if (!m && name) m = parseCommentMarkForName(d.comment, name);
    if (m === 'deny') return 'deny';
    if (m === 'approve') sawApprove = true;
  }
  return sawApprove ? 'approve' : null;
}

function parseCommentMarkForName(comment, name) {
  if (!comment) return null;
  for (const rawLine of comment.split('\n')) {
    const line = rawLine.trim();
    if (line.startsWith('Needs revision:')) {
      const names = line.slice('Needs revision:'.length).split(',').map(s => s.trim());
      if (names.includes(name)) return 'deny';
    } else if (line.startsWith('OK:')) {
      const names = line.slice('OK:'.length).split(',').map(s => s.trim());
      if (names.includes(name)) return 'approve';
    }
  }
  return null;
}

// Renders the attachments list. Once a decision exists, buckets the files
// into Approved / Denied / Pending sections so the team sees exactly which
// file(s) got the ✓ instead of having to scan a flat list.
function AttachmentsBlock({ item }) {
  const atts = item.attachments || [];
  const hasDecisions = (item.decisions || []).length > 0;

  if (!hasDecisions) {
    return (
      <>
        <Label>Attachments</Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {atts.map(a => (
            <AttachmentRow key={a.url} attachment={a} mark={null} />
          ))}
        </div>
      </>
    );
  }

  const buckets = { approve: [], deny: [], pending: [] };
  for (const a of atts) {
    const m = aggregateAttachmentMark(item, a.url);
    if (m === 'approve') buckets.approve.push(a);
    else if (m === 'deny') buckets.deny.push(a);
    else buckets.pending.push(a);
  }

  const sections = [
    { key: 'approve', label: 'Approved', color: '#10B981', atts: buckets.approve },
    { key: 'deny',    label: 'Denied',   color: '#E11D48', atts: buckets.deny },
    { key: 'pending', label: 'Pending',  color: '#F59E0B', atts: buckets.pending },
  ].filter(s => s.atts.length > 0);

  return (
    <div style={{ marginBottom: 16 }}>
      <Label>Attachments</Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {sections.map(s => (
          <div key={s.key}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 11, fontWeight: 900, letterSpacing: 1.2,
              textTransform: 'uppercase', color: s.color, marginBottom: 6,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: s.color }} />
              {s.label} · {s.atts.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {s.atts.map(a => (
                <AttachmentRow key={a.url} attachment={a} mark={s.key === 'pending' ? null : s.key} isPending={s.key === 'pending'} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AttachmentRow({ attachment, mark, isPending }) {
  const rowTint =
    mark === 'approve' ? { bg: '#D1FAE5', border: 'rgba(16,185,129,0.35)' }
    : mark === 'deny'  ? { bg: '#FEE2E2', border: 'rgba(225,29,72,0.35)' }
    : isPending        ? { bg: '#FEF3C7', border: 'rgba(245,158,11,0.4)' }
    : null;
  const isVoice = isVoiceAttachment(attachment);
  const icon = isVoice ? 'fa-waveform'
             : attachment.type?.startsWith('image/') ? 'fa-image'
             : 'fa-file-pdf';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderRadius: 8,
                  background: rowTint?.bg || 'var(--bg)',
                  border: `1px solid ${rowTint?.border || 'var(--border)'}`,
                  color: 'var(--text2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className={`fa-solid ${icon}`} style={{ color: isVoice ? '#7C3AED' : 'var(--blue)' }} />
        <span style={{ flex: 1, fontSize: 13 }}>{attachment.name}</span>
        {mark
          ? <AttachmentMarkChip mark={mark} />
          : isPending
            ? <AttachmentPendingChip />
            : null}
        <a href={attachment.url} target="_blank" rel="noreferrer"
           style={{ color: 'var(--text3)', textDecoration: 'none' }}>
          <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 11 }} />
        </a>
      </div>
      {isVoice && (
        <audio controls src={attachment.url} style={{ width: '100%', height: 32 }} />
      )}
    </div>
  );
}

function isVoiceAttachment(a) {
  if (a?.type?.startsWith('audio/')) return true;
  const lower = (a?.name || '').toLowerCase();
  return lower.endsWith('.m4a') || lower.endsWith('.mp3') || lower.endsWith('.wav') || lower.endsWith('.aac');
}

function AttachmentMarkChip({ mark }) {
  const approve = mark === 'approve';
  return (
    <span
      title={approve ? 'Approved by at least one approver' : 'At least one approver flagged this file'}
      style={{
        width: 18, height: 18, borderRadius: 999, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        background: approve ? '#10B981' : '#E11D48',
        color: '#fff', fontSize: 10, fontWeight: 900,
      }}
    >
      <i className={`fa-solid ${approve ? 'fa-check' : 'fa-xmark'}`} />
    </span>
  );
}

// Amber pill shown next to files that were left unmarked when a decision was
// made on the overall approval — signals "nobody explicitly signed this off".
function AttachmentPendingChip() {
  return (
    <span
      title="No approver marked this file — not part of the overall approval."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#FEF3C7', color: '#9A6300',
        fontSize: 9, fontWeight: 900, letterSpacing: 0.5,
        padding: '3px 7px', borderRadius: 999,
        border: '1px solid rgba(245,158,11,0.5)',
      }}
    >
      <i className="fa-solid fa-clock" style={{ fontSize: 8 }} />
      PENDING
    </span>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_TABS.find(t => t.id === status) || STATUS_TABS[3];
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
      color: '#fff', background: meta.tint, padding: '2px 8px', borderRadius: 999,
    }}>{meta.label}</span>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                  color: 'var(--text3)', marginBottom: 6, marginTop: 4 }}>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)',
  background: 'var(--bg)', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
  outline: 'none', marginBottom: 14, boxSizing: 'border-box',
};

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Drag-drop / click-to-browse / take-photo / paste-to-upload zone. Callers pass
// `attachments` + setter; we handle the POST /uploads round-trip and append
// successful uploads. `onError` surfaces messages to the parent form state.
function AttachmentDropzone({ attachments, setAttachments, onError, compact = false }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading]   = useState(0);
  const fileInputRef   = useRef(null);
  const cameraInputRef = useRef(null);
  const zoneRef        = useRef(null);

  const uploadFiles = async (files) => {
    for (const f of files) {
      setUploading(n => n + 1);
      try {
        const data = await readAsBase64(f);
        const res  = await apiFetch('/uploads', {
          method: 'POST',
          body: JSON.stringify({ name: f.name, type: f.type, data }),
        });
        setAttachments(prev => [...prev, { name: f.name, type: f.type, url: res.url, size: res.size }]);
      } catch (e) {
        onError?.('Upload failed: ' + e.message);
      } finally {
        setUploading(n => n - 1);
      }
    }
  };

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = (e) => {
    // Only clear when leaving the zone itself, not when moving over children
    if (e.currentTarget === e.target) setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = [...(e.dataTransfer?.files || [])];
    if (files.length) uploadFiles(files);
  };

  // Paste support — Cmd/Ctrl+V of a screenshot pushes it straight into uploads.
  useEffect(() => {
    const onPaste = (e) => {
      // Skip if user is pasting into a text input/textarea, that's plain text
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const items = [...(e.clipboardData?.items || [])];
      const files = items.map(i => i.kind === 'file' ? i.getAsFile() : null).filter(Boolean);
      if (files.length) { e.preventDefault(); uploadFiles(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const removeAttachment = (url) => {
    setAttachments(prev => prev.filter(a => a.url !== url));
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      <div
        ref={zoneRef}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragActive ? 'var(--blue)' : 'var(--border)'}`,
          background: dragActive ? 'rgba(30,64,175,0.08)' : 'var(--bg)',
          borderRadius: 12,
          padding: compact ? '14px 12px' : '20px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s',
          marginBottom: 10,
        }}
      >
        <i className="fa-solid fa-cloud-arrow-up"
           style={{ fontSize: compact ? 18 : 24, color: dragActive ? 'var(--blue)' : 'var(--text3)', marginBottom: 6, display: 'block' }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text2)', marginBottom: 2 }}>
          {dragActive ? 'Drop to upload' : (isMobile ? 'Tap to choose files' : 'Drag & drop, click to browse, or paste')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Images (JPG, PNG, HEIC) or PDF
        </div>
        <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf"
               onChange={e => { uploadFiles([...(e.target.files || [])]); e.target.value = ''; }}
               style={{ display: 'none' }} />
      </div>

      {isMobile && (
        <button type="button" onClick={() => cameraInputRef.current?.click()}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 10, marginBottom: 10,
            background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text2)',
            fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          <i className="fa-solid fa-camera" /> Take Photo
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                 onChange={e => { uploadFiles([...(e.target.files || [])]); e.target.value = ''; }}
                 style={{ display: 'none' }} />
        </button>
      )}

      {uploading > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }} />
          Uploading {uploading} file{uploading > 1 ? 's' : ''}…
        </div>
      )}

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {attachments.map(a => (
            <div key={a.url} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderRadius: 8,
              background: 'var(--bg)', border: '1px solid var(--border)',
            }}>
              {a.type?.startsWith('image/') ? (
                <img src={(process.env.REACT_APP_API_URL || '') + a.url} alt={a.name}
                     style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />
              ) : (
                <i className="fa-solid fa-file-pdf" style={{ color: 'var(--blue)', fontSize: 20, width: 36, textAlign: 'center' }} />
              )}
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text2)', overflow: 'hidden',
                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
              <button type="button" onClick={() => removeAttachment(a.url)}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
